import { useLayoutEffect, useRef } from "react";

/** Space kept clear between the tagline and the first header button, beyond the header gap. */
const BREATHING_ROOM = 24;

/**
 * Show the header tagline on one line while it fits between the logo and the
 * navigation, on two lines once it no longer does, and only for screen readers
 * when even two lines would crowd the buttons. The navigation's width differs
 * by page and the tagline's by theme, so this measures instead of using
 * breakpoints. Two hidden rulers hold the tagline at its one- and two-line
 * sizes; the header's `data-tagline` attribute selects the styling.
 */
export function useTaglineFit<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const header = ref.current;
    const logo = header?.querySelector<HTMLElement>(".logo");
    const nav = header?.querySelector<HTMLElement>(".site-nav");
    const one = header?.querySelector<HTMLElement>(".tagline-ruler-one");
    const two = header?.querySelector<HTMLElement>(".tagline-ruler-two");
    if (!header || !logo || !nav || !one || !two) return;

    const fit = () => {
      const style = getComputedStyle(header);
      const gap = parseFloat(style.columnGap) || 0;
      const inner =
        header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const room = inner - logo.offsetWidth - nav.scrollWidth - 2 * gap - BREATHING_ROOM;
      // Phones lay the header out as a grid of buttons with no place for the tagline.
      const mode =
        style.display !== "flex"
          ? "none"
          : one.offsetWidth <= room
            ? "one"
            : two.offsetWidth <= room
              ? "two"
              : "none";
      if (header.dataset.tagline !== mode) header.dataset.tagline = mode;
    };

    fit();
    // The rulers change width with the theme and once web fonts load, the
    // navigation with the page. The tagline never resizes either of them.
    const observer = new ResizeObserver(fit);
    for (const element of [nav, one, two]) observer.observe(element);
    window.addEventListener("resize", fit);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);

  return ref;
}
